import { logger } from "@sentry/react";

import { safelyExecute } from "common/utilities/safelyExecute";
import type { LoginRadius } from "types/globals";

type AuthWidget = {
  initAuthentication: (config: AuthWidgetConfig) => LoginRadius;
  render: () => void;
};

type AuthWidgetConfig = {
  accountStateUrl: string;
  apiBaseUrl: string;
  apiKey: string;
  appName: string;
  clientId: string;
  debug: boolean;
  fwsPasswordlessUrl: string;
  initiativeId?: string;
  lang: string;
  memberOnboardingToken?: string;
  onboardingFlowMode?: boolean;
  onboardingInitUrl?: string;
  onboardingLoadingPageUrl?: string;
  passwordlessLoginEmailTemplate?: string;
  passwordlessRedirectUrl: string;
  policyUrl?: string;
  registrationFormOnly?: boolean;
  resetPasswordEmailTemplate?: string;
  resetPasswordUrl: string;
  rootSelector: string;
  signinOnly?: boolean;
  socialSignupUrl?: string;
  sott?: string;
  termsUrl?: string;
  verificationEmailTemplate?: string;
};

export const onAuthWidgetLoad = (callback: (authWidget: AuthWidget) => void) => {
  const authWidget = getAuthWidget();
  if (authWidget !== undefined) return safelyExecute(callback, authWidget);

  let attempts = 1;

  const interval = setInterval(() => {
    if (attempts++ === MAX_ATTEMPTS) {
      logger.warn(
        `Timed out after ${TIMEOUT / (60 * 1000)}s and ${MAX_ATTEMPTS} attempt(s) waiting for AuthWidget to be available`,
      );
      clearInterval(interval);
      return;
    }
    const authWidget = getAuthWidget();
    if (authWidget === undefined) return;
    clearInterval(interval);
    safelyExecute(callback, authWidget);
  }, INTERVAL);
};

const getAuthWidget = () => {
  const { AuthWidget } = window;

  if (typeof AuthWidget !== "object") {
    logger.warn("AuthWidget is not an object");
    return undefined;
  }
  if (AuthWidget === null) {
    logger.warn("AuthWidget is null");
    return undefined;
  }
  if (!("initAuthentication" in AuthWidget)) {
    logger.warn("AuthWidget does not contain initAuthentication property");
    return undefined;
  }
  if (!("render" in AuthWidget)) {
    logger.warn("AuthWidget does not contain render property");
    return undefined;
  }

  const { initAuthentication, render } = AuthWidget;
  if (typeof initAuthentication !== "function") {
    logger.warn("initAuthentication property in AuthWidget is not a function");
    return undefined;
  }
  if (typeof render !== "function") {
    logger.warn("render property in AuthWidget is not a function");
    return undefined;
  }

  // There is no good way of checking the signatures of these functions at runtime
  return { initAuthentication, render } as AuthWidget;
};

const INTERVAL = 200;
const TIMEOUT = 10 * 60 * 1000;
const MAX_ATTEMPTS = 1 + TIMEOUT / INTERVAL;
